// built-from: /discovery-phase-workspace v1 impl:discovery-phase-workspace role:implementation
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ActivityState } from '../fixtures/modernisation/activities';
import type { LumenGridModernisationJourney } from '../fixtures/modernisation/scenarios';
import type { DurableDiscoveryActivity, UseDiscoveryPlanReturn } from '../hooks/useDiscoveryPlan';

type Props = {
  journey: LumenGridModernisationJourney;
  activityPath: string;
  startMappingPath?: string;
  engagementName?: string;
  discoveryPlan?: UseDiscoveryPlanReturn;
  proposalAccepted?: boolean;
  onAcceptProposal?: () => void;
};

const statusLabels: Record<ActivityState, string> = {
  proposed: 'Proposed',
  planned: 'Planned',
  'in-progress': 'In progress',
  'awaiting-review': 'Awaiting review',
  complete: 'Complete',
};

function nextAction(activity: DurableDiscoveryActivity): string {
  if (activity.state === 'complete') return 'Output available';
  if (activity.state === 'awaiting-review') return 'Output review pending';
  if (activity.state === 'in-progress') return 'Continue activity';
  return 'Ready to start';
}

export function DiscoveryPhaseWorkspace({
  journey,
  activityPath,
  startMappingPath = '/engagement/discovery/current-state-mapping/start',
  engagementName,
  discoveryPlan,
  proposalAccepted = false,
  onAcceptProposal = () => {},
}: Props): JSX.Element {
  const [reviewingScope, setReviewingScope] = useState(false);
  const standaloneProposed = journey.activities.find((activity) => activity.state === 'proposed');
  const standalonePlan: UseDiscoveryPlanReturn = {
    plan: {
      version: 0,
      scopeDecisions: standaloneProposed && !proposalAccepted ? [{
        id: 'decision-technical-estate',
        state: 'proposed',
        definitionId: standaloneProposed.definition.id,
      }] : [],
      activities: journey.activities
        .filter((activity) => activity.state !== 'proposed' || proposalAccepted)
        .map((activity) => ({
          id: activity.id,
          sourceDecisionId: activity.state === 'proposed' ? 'decision-technical-estate' : undefined,
          definition: {
            id: activity.definition.id,
            name: activity.definition.name,
            method: activity.definition.method,
            purpose: activity.definition.purpose,
          },
          owner: { name: activity.owner.name },
          expectedOutput: { name: activity.output.name },
          rationale: activity.progress,
          state: activity.state === 'proposed' ? 'planned' : activity.state,
        })),
    },
    revision: 'standalone',
    loading: false,
    pendingDecisionId: null,
    error: null,
    resolveDecision: async (_decisionId, outcome) => {
      if (outcome === 'accept') onAcceptProposal();
    },
    retryDecision: async () => {},
    loadLatestPlan: async () => {},
    pendingActivityCommand: null,
    activityOutputError: null,
    linkActivitySession: async () => false,
    returnActivityOutput: async () => false,
    requestActivityOutputChanges: async () => false,
    reviewActivityOutput: async () => false,
    currentStateMappingStatus: 'idle',
    currentStateMappingError: null,
    pendingCurrentStateMapping: null,
    startCurrentStateMapping: async () => null,
  };
  const {
    plan,
    loading,
    pendingDecisionId,
    error,
    resolveDecision,
    retryDecision,
    loadLatestPlan,
    currentStateMappingStatus,
  } = discoveryPlan ?? standalonePlan;
  const proposedDecision = plan?.scopeDecisions.find((decision) => decision.state === 'proposed');
  const proposedDecisionCount = plan?.scopeDecisions.filter(
    (decision) => decision.state === 'proposed',
  ).length ?? 0;
  const proposed = proposedDecision
    ? journey.activities.find((activity) => activity.definition.id === proposedDecision.definitionId)
    : undefined;
  const displayedActivities = (plan?.activities ?? []).filter((activity) => activity.state !== 'proposed');

  const ownerName = (activity: DurableDiscoveryActivity): string => {
    if (typeof activity.owner === 'string') return activity.owner;
    return activity.owner?.name ?? 'Owner to confirm';
  };

  return (
    <div className="journey-page">
      <header className="journey-page__header">
        <span className="journey-eyebrow">Current phase</span>
        <h1>Discovery</h1>
        <p>Choose and complete the work needed to understand {engagementName || 'this engagement'} before recommending change.</p>
      </header>

      {loading ? (
        <section className="journey-panel" aria-live="polite">
          <div className="journey-empty-state" role="status">Loading Discovery plan…</div>
        </section>
      ) : !plan ? (
        <section className="journey-panel" aria-live="polite">
          <div className="journey-empty-state" role="alert">
            <strong>{error?.message ?? 'Discovery plan is unavailable.'}</strong>
            <span>The current scope and activities have not been loaded.</span>
            <button
              className="journey-button journey-button--secondary"
              type="button"
              onClick={() => void loadLatestPlan()}
            >
              Retry
            </button>
          </div>
        </section>
      ) : (
        <>

      <section id="scope-decisions" className="journey-panel journey-scope" aria-labelledby="scope-title">
        <div className="journey-section-heading">
          <div>
            <span className="journey-eyebrow">Changes to agreed work</span>
            <h2 id="scope-title">Scope decisions</h2>
            <p>Suggestions stay outside Discovery until somebody explicitly brings them into scope.</p>
          </div>
          <span className="journey-count">{proposedDecisionCount} open</span>
        </div>

        {proposed && proposedDecision ? (
          <div className={`journey-scope-decision${reviewingScope ? ' journey-scope-decision--open' : ''}`}>
            <div className="journey-scope-decision__summary">
              <div>
                <div className="journey-title-line">
                  <strong>{proposed.definition.name}</strong>
                  <span className="journey-status journey-status--decision">Decision needed</span>
                </div>
                <p>{'source' in proposed ? proposed.source : 'Proposed from Discovery evidence'}</p>
              </div>
              <button className="journey-button journey-button--secondary" type="button" onClick={() => setReviewingScope((open) => !open)}>
                {reviewingScope ? 'Close review' : 'Review scope'}
              </button>
            </div>

            {reviewingScope ? (
              <div className="journey-scope-review">
                <div><span>Why consider it</span><p>{proposed.definition.purpose}</p></div>
                <div><span>Method</span><p>{proposed.definition.method}</p></div>
                <div><span>Expected output</span><p>{proposed.output.name}</p></div>
                <div className="journey-scope-review__actions">
                  <button
                    className="journey-button journey-button--secondary"
                    type="button"
                    disabled={pendingDecisionId === proposedDecision.id}
                    onClick={() => void resolveDecision(proposedDecision.id, 'reject')}
                  >
                    Keep outside scope
                  </button>
                  <button
                    className="journey-button journey-button--primary"
                    type="button"
                    disabled={pendingDecisionId === proposedDecision.id}
                    onClick={() => void resolveDecision(proposedDecision.id, 'accept')}
                  >
                    {pendingDecisionId === proposedDecision.id ? 'Saving…' : 'Add to Discovery'}
                  </button>
                </div>
              </div>
            ) : null}
            {error?.decisionId === proposedDecision.id ? (
              <div className="journey-empty-state" role="alert">
                <strong>Decision not saved</strong>
                <span>{error.message}</span>
                <button
                  className="journey-button journey-button--secondary"
                  type="button"
                  onClick={() => {
                    if (error.conflict) void loadLatestPlan();
                    else void retryDecision(proposedDecision.id);
                  }}
                >
                  {error.conflict ? 'Load latest plan' : 'Retry'}
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="journey-empty-state" role="status">
            <strong>No scope decisions are waiting.</strong>
            <span>There are no proposed changes to the agreed Discovery work.</span>
          </div>
        )}
      </section>

      <section className="journey-panel journey-activities" aria-labelledby="activities-title">
        <div className="journey-section-heading">
          <div>
            <span className="journey-eyebrow">Agreed work</span>
            <h2 id="activities-title">Activities in scope</h2>
            <p>Every row is one activity, regardless of the method used to complete it.</p>
          </div>
          <span className="journey-count">{displayedActivities.length} activities</span>
        </div>

        <div className="journey-activity-list">
          <div className="journey-activity-list__header" aria-hidden="true">
            <span>Activity</span><span>Owner and method</span><span>State</span><span>Next action</span>
          </div>
          {displayedActivities.map((activity) => {
            const isMappingActivity = activity.definition.id === 'map-current-value-streams';
            const canonicalActivity = journey.activities.find(
              (candidate) => candidate.definition.id === activity.definition.id,
            );
            return (
              <article className={`journey-activity-row${isMappingActivity ? ' journey-activity-row--actionable' : ''}`} key={activity.id}>
                <div className="journey-activity-row__title">
                  <strong>{activity.definition.name}</strong>
                  <small>{canonicalActivity?.progress ?? activity.rationale ?? 'Ready to schedule'}</small>
                </div>
                <div className="journey-activity-row__meta">
                  <span>{ownerName(activity)}</span>
                  <small>{activity.definition.method}</small>
                </div>
                <span className={`journey-status journey-status--${activity.state}`}>{statusLabels[activity.state]}</span>
                {isMappingActivity ? (
                  <Link className="journey-button journey-button--primary" to={activityPath}>Open activity</Link>
                ) : (
                  <span className="journey-next-label">{nextAction(activity)}</span>
                )}
              </article>
            );
          })}
          {displayedActivities.length === 0 ? (
            <div className="journey-empty-state" role="status">
              <strong>{currentStateMappingStatus === 'recovery'
                ? 'Mapping setup needs finishing'
                : 'No Discovery activities yet'}</strong>
              <span>{currentStateMappingStatus === 'recovery'
                ? 'Your details are safe, and the activity is not visible yet.'
                : 'Start with the work needed now; add other activities as evidence shows they are needed.'}</span>
              <Link className="journey-button journey-button--primary" to={startMappingPath}>
                {currentStateMappingStatus === 'recovery'
                  ? 'Finish starting mapping'
                  : 'Start current-state mapping'}
              </Link>
            </div>
          ) : null}
        </div>
      </section>
        </>
      )}
    </div>
  );
}
